// src/utils/ndjsonStream.ts
// ────────────────────────────────────────────────────────────────────────────
// Canonical NDJSON consumer for the FE.  Sibling of the BFF
// `ndjson-writer.mjs` primitive.
//
// Reads a `fetch()`-style Response whose body is `application/x-ndjson`,
// parses each line as JSON, and either yields frames via an async
// iterator OR dispatches them through an `onFrame` callback.
//
// Wire contract (matches BFF `ndjson-writer.mjs`):
//   • Each line is a complete JSON value, terminated by `\n`.
//   • Empty lines are heartbeats — silently skipped.
//   • Partial last line (no trailing `\n`) is preserved across reads
//     and parsed when the next chunk completes it (RFC-conformant).
//   • A line that fails JSON.parse is reported via `onParseError`
//     (or swallowed when no handler is registered) — never throws,
//     so a single corrupt frame cannot kill the stream.
//
// Cancellation:
//   • Accepts an AbortSignal.  When aborted, the underlying reader is
//     released and `consumeNdjsonStream` resolves cleanly.
//
// Why a primitive here:
//   • Every future BFF NDJSON consumer on the FE (video gallery, agent
//     chat tool results, operator-digest list, supplier catalog, …)
//     gets identical RFC-correct line splitting, heartbeat handling,
//     and abort semantics from one source of truth (DRY).
//   • Zero dependencies — uses native `ReadableStream` + `TextDecoder`.
// ────────────────────────────────────────────────────────────────────────────

export interface NdjsonStreamOptions<T = unknown> {
  /** Called for each successfully parsed frame. */
  onFrame?: (frame: T) => void | Promise<void>;
  /** Called when a line cannot be parsed as JSON.  Default: silent. */
  onParseError?: (line: string, err: unknown) => void;
  /** Abort the read loop. */
  signal?: AbortSignal;
}

/**
 * Consume an NDJSON `Response` body to completion, dispatching parsed
 * frames through `onFrame`.  Resolves when the stream ends or `signal`
 * fires.  Rejects only on transport errors (network, decoder errors);
 * per-frame parse errors are reported through `onParseError` so the
 * stream survives a single corrupt frame.
 */
export async function consumeNdjsonStream<T = unknown>(
  response: Response,
  opts: NdjsonStreamOptions<T> = {},
): Promise<void> {
  if (!response.body) {
    throw new Error('consumeNdjsonStream: response has no body');
  }

  const { onFrame, onParseError, signal } = opts;
  const reader  = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  // Wire abort to cancel the reader — releases the underlying socket
  // without waiting for the next chunk.
  const abortHandler = () => {
    try { reader.cancel('abort'); } catch { /* already cancelled */ }
  };
  if (signal) {
    if (signal.aborted) {
      try { reader.cancel('abort'); } catch { /* ignore */ }
      return;
    }
    signal.addEventListener('abort', abortHandler, { once: true });
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split off complete lines; keep the trailing partial in `buffer`.
      let nl;
      while ((nl = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 1);
        if (line.length === 0) continue;     // heartbeat — silent skip
        let frame: T;
        try {
          frame = JSON.parse(line) as T;
        } catch (err) {
          onParseError?.(line, err);
          continue;
        }
        if (onFrame) {
          const r = onFrame(frame);
          if (r && typeof (r as Promise<void>).then === 'function') {
            await r;
          }
        }
      }
    }

    // Flush any trailing partial frame (server omitted final \n).
    buffer += decoder.decode();
    const tail = buffer.trim();
    if (tail) {
      try {
        const frame = JSON.parse(tail) as T;
        if (onFrame) await onFrame(frame);
      } catch (err) {
        onParseError?.(tail, err);
      }
    }
  } finally {
    if (signal) signal.removeEventListener('abort', abortHandler);
    try { reader.releaseLock(); } catch { /* already released */ }
  }
}
